import * as Store from '@apply/store';
import { connect } from 'react-redux';
import { QuestionValidateType, ShowIf } from '@apply/ui';
import {
    AcquisitionFinancingRental,
    CapitalExpenditures,
    CapitalExpendituresFile,
    CurrentDebtRental,
    DateOfPurchase,
    PreferredTerm,
    PurchaseContractFile,
    SiteBuiltOrManufacturedRental,
    StructureAndRentalUnits,
    TargetLoanRequestRental,
    PropertyAddress,
    YearBuiltRental,
    LoanProceedPropertyImprovementRental,
} from '../questions/index';
import { RentalUnits } from '../../RentalUnits';

export interface RentalProps {
    onQuestionValidate?: QuestionValidateType;
    submitted: boolean;
    handleFileSelected: (
        fileType: string,
        file: File,
        updater: (guid: string, value: Store.PropertiesStore.Types.Attachment) => unknown
    ) => Promise<void>;
    property: Store.PropertiesStore.Types.Property;
    onChangeRentalUnit: (guid: string, value: Store.PropertiesStore.Types.RentalUnit) => void;
    onChangeProvideRentalUnitsLater: (guid: string, value: boolean) => void;
}

const Rental: React.FC<RentalProps> = ({
    onQuestionValidate,
    submitted,
    handleFileSelected,
    property,
    onChangeRentalUnit,
    onChangeProvideRentalUnitsLater,
}) => {
    const isPurchaseDateLessThanTwoYears = (): boolean => {
        if (!property?.rental?.purchaseDate) return false;

        const purchaseYearMonthDay = property?.rental?.purchaseDate.replace('-', '').replace('-', '');

        const twoYearsBeforeToday = new Date();
        twoYearsBeforeToday.setFullYear(twoYearsBeforeToday.getFullYear() - 2);

        const twoYearsBeforeYearMonthDay = `${twoYearsBeforeToday.getFullYear()}${(twoYearsBeforeToday.getMonth() + 1).toString().padStart(2, '0')}${twoYearsBeforeToday.getDate().toString().padStart(2, '0')}`;
        return purchaseYearMonthDay > twoYearsBeforeYearMonthDay; // i.e. '20220619' > '20220630' (YYYYMMDD)
    };

    return (
        <>
            <PropertyAddress
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <YearBuiltRental
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <SiteBuiltOrManufacturedRental
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <AcquisitionFinancingRental
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <ShowIf showIf={property.rental?.isAquisitionFinancing === false}>
                <CurrentDebtRental
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    required={true}
                />
                <ShowIf
                    showIf={property.rental?.currentDebt?.toString() === '0' || property.rental?.currentDebt?.toString() === ''}
                >
                    <LoanProceedPropertyImprovementRental
                        onQuestionValidate={onQuestionValidate}
                        submitted={submitted}
                        required={true}
                    />
                </ShowIf>
                <DateOfPurchase
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    required={true}
                />
                <ShowIf showIf={isPurchaseDateLessThanTwoYears()}>
                    <PurchaseContractFile
                        onQuestionValidate={onQuestionValidate}
                        submitted={submitted}
                        handleFileSelected={handleFileSelected}
                        required={true}
                    />
                    <CapitalExpenditures
                        onQuestionValidate={onQuestionValidate}
                        submitted={submitted}
                        title='Have you done (or within six months will you do) more then $20,000 of capital expenditures?'
                        required={true}
                    />
                    <ShowIf showIf={property.rental?.moreThan10kExpenditures === true}>
                        <CapitalExpendituresFile
                            onQuestionValidate={onQuestionValidate}
                            submitted={submitted}
                            handleFileSelected={handleFileSelected}
                            required={true}
                        />
                    </ShowIf>
                </ShowIf>
                <ShowIf showIf={property.rental?.purchaseDate && !isPurchaseDateLessThanTwoYears()}>
                    <CapitalExpenditures
                        onQuestionValidate={onQuestionValidate}
                        submitted={submitted}
                        title='Within 6 months after closing this requested loan, do you plan over $20,000 of capital expenditures?'
                        required={true}
                    />
                    <ShowIf showIf={property.rental?.moreThan10kExpenditures === true}>
                        <CapitalExpendituresFile
                            onQuestionValidate={onQuestionValidate}
                            submitted={submitted}
                            handleFileSelected={handleFileSelected}
                            required={true}
                        />
                    </ShowIf>
                </ShowIf>
            </ShowIf>
            <ShowIf showIf={property.rental?.isAquisitionFinancing === true}>
                <PurchaseContractFile
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    required={true}
                />
                <CapitalExpenditures
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    title='In the first 6 months of ownership are over $20,000 of capital expenditures planned?'
                    required={true}
                />
                <ShowIf showIf={property.rental?.moreThan10kExpenditures === true}>
                    <CapitalExpendituresFile
                        onQuestionValidate={onQuestionValidate}
                        submitted={submitted}
                        handleFileSelected={handleFileSelected}
                        required={true}
                    />
                </ShowIf>
            </ShowIf>
            <TargetLoanRequestRental
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <PreferredTerm
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <StructureAndRentalUnits
                onQuestionValidate={onQuestionValidate}
                submitted={submitted}
                required={true}
            />
            <RentalUnits
                header={'Unit Information'}
                includeOccupied={true}
                units={property.rentalUnits}
                submitted={submitted}
                onChangeRentalUnit={onChangeRentalUnit}
                propertyGuid={property.guid}
                provideRentalUnitsLater={property.rental?.provideRentalUnitsLater}
                onChangeProvideRentalUnitsLater={onChangeProvideRentalUnitsLater}
                onQuestionValidate={onQuestionValidate}
            />
        </>
    );
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => {
    return {
        property: state.properties.items.find((itm) => itm.guid === state.properties.currentPropertyId),
    };
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: {
        onChangeRentalUnit: (guid: string, value: Store.PropertiesStore.Types.RentalUnit) => void;
        onChangeProvideRentalUnitsLater: (guid: string, value: boolean) => void;
        dispatch: Store.ApplyDispatchType;
    } = {
        onChangeRentalUnit: (guid: string, value: Store.PropertiesStore.Types.RentalUnit) =>
            dispatch(Store.PropertiesStore.Actions.updateRentalUnit(guid, value)),
        onChangeProvideRentalUnitsLater: (guid: string, value: boolean) =>
            dispatch(Store.PropertiesStore.Actions.updateProvideRentalUnitsLater(guid, value)),
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(Rental);
